'use client';

import { c as _c } from "react/compiler-runtime";
import { Card } from '@achra/ui/card';
import { useCallback, useMemo } from 'react';
import { RsGroupCostType } from '@/modules/__generated__/graphql/switchboard-generated';
import { ConfigureStepContinueButton } from '@/modules/service-purchase/components/configure-step-continue-button';
import { DEFAULT_PLAN_INDEX } from '@/modules/service-purchase/lib/constants';
import { computeTierHeaderPriceWithBreakdown, getGroupPriceFromBreakdown, getPriceBreakdown } from '@/modules/service-purchase/lib/price-breakdown-utils';
import { sortOptionGroups } from '@/modules/service-purchase/lib/utils';
import { PricingCalculatorProvider } from '@/modules/service-purchase/providers/pricing-calculator-provider';
import { useAllOptionGroups, useComputedTiers, useSelectedTier, useServiceOffering, useServicePurchaseActions, useServicePurchaseState } from '@/modules/service-purchase/providers/service-purchase-store-provider';
import { GrandTotalRowCatalog } from '../grand-total-row-catalog';
import { HeaderCatalogPlan } from '../header-catalog-plan';
import { OptionGroupSection } from './option-group-section';
function PricingCalculator() {
  const $ = _c(84);
  const servicesData = useServiceOffering();
  const tier = useSelectedTier();
  const tiers = useComputedTiers();
  const {
    setSelectedTier
  } = useServicePurchaseActions();
  const selectedPlan = tier.id;
  const storeOptionGroups = useAllOptionGroups();
  const {
    selectedBillingCycle: billingPeriod
  } = useServicePurchaseState();
  let t0;
  if ($[0] !== tiers) {
    t0 = tiers.map(_temp);
    $[0] = tiers;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const tierNames = t0;
  const idx = tierNames.indexOf(selectedPlan);
  const mobilePlanIndex = idx >= 0 ? idx : DEFAULT_PLAN_INDEX;
  let t1;
  if ($[2] !== mobilePlanIndex || $[3] !== setSelectedTier || $[4] !== tierNames) {
    t1 = () => {
      const newIndex = mobilePlanIndex > 0 ? mobilePlanIndex - 1 : tierNames.length - 1;
      setSelectedTier(tierNames[newIndex]);
    };
    $[2] = mobilePlanIndex;
    $[3] = setSelectedTier;
    $[4] = tierNames;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const handlePrevPlan = t1;
  let t2;
  if ($[6] !== mobilePlanIndex || $[7] !== setSelectedTier || $[8] !== tierNames) {
    t2 = () => {
      const newIndex_0 = mobilePlanIndex < tierNames.length - 1 ? mobilePlanIndex + 1 : 0;
      setSelectedTier(tierNames[newIndex_0]);
    };
    $[6] = mobilePlanIndex;
    $[7] = setSelectedTier;
    $[8] = tierNames;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const handleNextPlan = t2;
  let prices;
  if ($[10] !== billingPeriod || $[11] !== servicesData || $[12] !== storeOptionGroups || $[13] !== tiers) {
    const activeGroupIds = new Set(storeOptionGroups.filter(_temp2).map(_temp3));
    prices = {};
    for (const t_0 of tiers) {
      prices[t_0.id] = computeTierHeaderPriceWithBreakdown(servicesData, t_0.id, billingPeriod, activeGroupIds);
    }
    $[10] = billingPeriod;
    $[11] = servicesData;
    $[12] = storeOptionGroups;
    $[13] = tiers;
    $[14] = prices;
  } else {
    prices = $[14];
  }
  const tierHeaderPrices = prices;
  let prices_0;
  if ($[15] !== billingPeriod || $[16] !== servicesData || $[17] !== storeOptionGroups || $[18] !== tier.id) {
    const activeGroupIds_0 = new Set(storeOptionGroups.filter(_temp4).map(_temp5));
    const breakdown = getPriceBreakdown(servicesData, tier.id, billingPeriod, activeGroupIds_0);
    prices_0 = {};
    for (const section of servicesData.optionGroups) {
      if (section.isAddOn || section.costType !== RsGroupCostType.Setup) {
        continue;
      }
      const result = getGroupPriceFromBreakdown(breakdown, section.id, true);
      prices_0[section.id] = result?.amount ?? null;
    }
    $[15] = billingPeriod;
    $[16] = servicesData;
    $[17] = storeOptionGroups;
    $[18] = tier.id;
    $[19] = prices_0;
  } else {
    prices_0 = $[19];
  }
  const setupDiscountedPrices = prices_0;
  let t3;
  if ($[20] !== billingPeriod || $[21] !== handleNextPlan || $[22] !== handlePrevPlan || $[23] !== selectedPlan || $[24] !== tierHeaderPrices || $[25] !== tierNames || $[26] !== tiers) {
    t3 = {
      activePlan: selectedPlan,
      onPrevPlan: handlePrevPlan,
      onNextPlan: handleNextPlan,
      tierNames,
      tiers,
      selectedBillingCycle: billingPeriod,
      tierHeaderPrices
    };
    $[20] = billingPeriod;
    $[21] = handleNextPlan;
    $[22] = handlePrevPlan;
    $[23] = selectedPlan;
    $[24] = tierHeaderPrices;
    $[25] = tierNames;
    $[26] = tiers;
    $[27] = t3;
  } else {
    t3 = $[27];
  }
  const contextValue = t3;
  let T0;
  let t10;
  let t11;
  let t12;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[28] !== contextValue || $[29] !== handleNextPlan || $[30] !== handlePrevPlan || $[31] !== mobilePlanIndex || $[32] !== selectedPlan || $[33] !== servicesData || $[34] !== setSelectedTier || $[35] !== setupDiscountedPrices) {
    const sortedGroups = sortOptionGroups(servicesData.optionGroups);
    const setupGroups = sortedGroups.filter(_temp6);
    const recurringGroups = sortedGroups.filter(_temp7);
    const addOnGroups = sortedGroups.filter(_temp8);
    T0 = PricingCalculatorProvider;
    t12 = contextValue;
    t11 = "flex w-full flex-col gap-8";
    t4 = "flex flex-col";
    if ($[46] !== handleNextPlan || $[47] !== handlePrevPlan || $[48] !== mobilePlanIndex || $[49] !== selectedPlan || $[50] !== servicesData || $[51] !== setSelectedTier) {
      t5 = <HeaderCatalogPlan selectedPlan={selectedPlan} handlePlanChange={setSelectedTier} mobilePlanIndex={mobilePlanIndex} onPrevPlan={handlePrevPlan} onNextPlan={handleNextPlan} servicesData={servicesData} />;
      $[46] = handleNextPlan;
      $[47] = handlePrevPlan;
      $[48] = mobilePlanIndex;
      $[49] = selectedPlan;
      $[50] = servicesData;
      $[51] = setSelectedTier;
      $[52] = t5;
    } else {
      t5 = $[52];
    }
    if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <h2 className="text-muted-foreground mt-8 mb-3 text-xs font-bold tracking-wide uppercase">{"Setup & Formation"}</h2>;
      $[53] = t6;
    } else {
      t6 = $[53];
    }
    let t13;
    if ($[54] !== setupDiscountedPrices) {
      t13 = section_0 => <OptionGroupSection key={section_0.id} section={section_0} setupDiscountedPrices={setupDiscountedPrices} />;
      $[54] = setupDiscountedPrices;
      $[55] = t13;
    } else {
      t13 = $[55];
    }
    const t14 = <div className="overflow-clip rounded-xl"><div className="flex flex-col">{setupGroups.map(t13)}</div></div>;
    if ($[56] !== t14) {
      t7 = <div className="overflow-hidden rounded-xl border shadow-sm"><Card className="flex w-full flex-col border-none! py-0! shadow-none!">{t14}</Card></div>;
      $[56] = t14;
      $[57] = t7;
    } else {
      t7 = $[57];
    }
    if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <h2 className="text-muted-foreground mt-8 mb-3 text-xs font-bold tracking-wide uppercase">Recurring Services</h2>;
      $[58] = t8;
    } else {
      t8 = $[58];
    }
    let t15;
    if ($[59] !== setupDiscountedPrices) {
      t15 = section_1 => <OptionGroupSection key={section_1.id} section={section_1} setupDiscountedPrices={setupDiscountedPrices} />;
      $[59] = setupDiscountedPrices;
      $[60] = t15;
    } else {
      t15 = $[60];
    }
    const t16 = <div className="overflow-clip rounded-b-xl"><div className="flex flex-col">{recurringGroups.map(t15)}</div></div>;
    if ($[61] !== t16) {
      t9 = <div className="overflow-hidden rounded-xl border shadow-sm"><Card className="flex w-full flex-col border-none! py-0! shadow-none!">{t16}</Card></div>;
      $[61] = t16;
      $[62] = t9;
    } else {
      t9 = $[62];
    }
    t10 = addOnGroups.length > 0 && <div className="mt-8 flex flex-col gap-4"><h2 className="text-muted-foreground mb-3 text-xs font-bold tracking-wide uppercase">ADD-ON</h2>{addOnGroups.map(section_2 => <div key={section_2.id} className="overflow-hidden rounded-xl border shadow-sm"><Card className="flex w-full flex-col border-none! py-0! shadow-none!"><div className="overflow-clip rounded-xl"><div className="flex flex-col"><OptionGroupSection section={section_2} setupDiscountedPrices={setupDiscountedPrices} /></div></div></Card></div>)}</div>;
    $[28] = contextValue;
    $[29] = handleNextPlan;
    $[30] = handlePrevPlan;
    $[31] = mobilePlanIndex;
    $[32] = selectedPlan;
    $[33] = servicesData;
    $[34] = setSelectedTier;
    $[35] = setupDiscountedPrices;
    $[36] = T0;
    $[37] = t10;
    $[38] = t11;
    $[39] = t12;
    $[40] = t4;
    $[41] = t5;
    $[42] = t6;
    $[43] = t7;
    $[44] = t8;
    $[45] = t9;
  } else {
    T0 = $[36];
    t10 = $[37];
    t11 = $[38];
    t12 = $[39];
    t4 = $[40];
    t5 = $[41];
    t6 = $[42];
    t7 = $[43];
    t8 = $[44];
    t9 = $[45];
  }
  let t13;
  if ($[63] !== t10 || $[64] !== t4 || $[65] !== t5 || $[66] !== t6 || $[67] !== t7 || $[68] !== t8 || $[69] !== t9) {
    t13 = <div className={t4}>{t5}{t6}{t7}{t8}{t9}{t10}</div>;
    $[63] = t10;
    $[64] = t4;
    $[65] = t5;
    $[66] = t6;
    $[67] = t7;
    $[68] = t8;
    $[69] = t9;
    $[70] = t13;
  } else {
    t13 = $[70];
  }
  let t14;
  if ($[71] !== selectedPlan || $[72] !== servicesData || $[73] !== tiers) {
    t14 = <GrandTotalRowCatalog selectedPlan={selectedPlan} tiers={tiers} offering={servicesData} />;
    $[71] = selectedPlan;
    $[72] = servicesData;
    $[73] = tiers;
    $[74] = t14;
  } else {
    t14 = $[74];
  }
  let t15;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <ConfigureStepContinueButton />;
    $[75] = t15;
  } else {
    t15 = $[75];
  }
  let t16;
  if ($[76] !== t11 || $[77] !== t13 || $[78] !== t14) {
    t16 = <div className={t11}>{t13}{t14}{t15}</div>;
    $[76] = t11;
    $[77] = t13;
    $[78] = t14;
    $[79] = t16;
  } else {
    t16 = $[79];
  }
  let t17;
  if ($[80] !== T0 || $[81] !== t12 || $[82] !== t16) {
    t17 = <T0 value={t12}>{t16}</T0>;
    $[80] = T0;
    $[81] = t12;
    $[82] = t16;
    $[83] = t17;
  } else {
    t17 = $[83];
  }
  return t17;
}
function _temp8(g_5) {
  return g_5.isAddOn;
}
function _temp7(g_4) {
  return g_4.costType !== RsGroupCostType.Setup && !g_4.isAddOn;
}
function _temp6(g_3) {
  return g_3.costType === RsGroupCostType.Setup && !g_3.isAddOn;
}
function _temp5(g_2) {
  return g_2.id;
}
function _temp4(g_1) {
  return g_1.isSelected;
}
function _temp3(g_0) {
  return g_0.id;
}
function _temp2(g) {
  return g.isSelected;
}
function _temp(t) {
  return t.id;
}
export { PricingCalculator };